import {
  wishListResponseSchema,
  wishSchema,
  wishSearchNowResponseSchema,
  wishSearchSettingSchema,
  type WishSearchNowResponse,
  type WishSearchRequest,
  type WishSearchSetting,
  type Wish,
  type WishRequest,
  cellarListResponseSchema,
  cellarStatisticsSchema,
  cellarSummarySchema,
  cellarTransferSchema,
  cellarTransfersResponseSchema,
  type CellarListResponse,
  type CellarTransfer,
  type CellarTransfersResponse,
  sessionResponseSchema,
  systemSettingsSchema,
  areaReportAdminListResponseSchema,
  areaReportIndexResponseSchema,
  type AreaReportIndexResponse,
  type ApplyAreaFindingsRequest,
  areaReportSchema,
  areaReportSummarySchema,
  publishedAreaReportListResponseSchema,
  type AreaReport,
  type AreaReportAdminListResponse,
  type AreaReportSummary,
  type PublishedAreaReportListResponse,
  type RunAreaReportRequest,
  myAreaWishListResponseSchema,
  openAreaWishListResponseSchema,
  type DeclineAreaWishesRequest,
  type MyAreaWishListResponse,
  type OpenAreaWishListResponse,
  type WishAreaReportRequest,
  type SystemSettings,
  cellarOverridesSchema,
  systemAccountListResponseSchema,
  systemCellarListResponseSchema,
  type CellarOverrides,
  type SystemAccountListResponse,
  type SystemCellarListResponse,
  shelfSummarySchema,
  storageListResponseSchema,
  storageSummarySchema,
  type CellarStatistics,
  type CellarSummary,
  renameVocabularyResponseSchema,
  vocabularyResponseSchema,
  type RenameVocabularyResponse,
  type VocabularyKind,
  type VocabularyResponse,
  type CreateShelfRequest,
  type CreateStorageRequest,
  type LoginRequest,
  type SessionUser,
  type ShelfSummary,
  type StorageSummary,
  type CreateWineRequest,
  type WineSummary,
  cellarNotesResponseSchema,
  recentlyDrunkResponseSchema,
  type RecentlyDrunkResponse,
  wineListResponseSchema,
  type CellarNotesResponse,
  type WineListResponse,
  type WineListRow,
  wineSummarySchema,
  labelSilenceResponseSchema,
  type LabelFact,
  type SetLabelSilenceRequest,
  wineDetailSchema,
  lotSummarySchema,
  type WineDetail,
  type CreateLotRequest,
  type LotSummary,
  suggestionsSchema,
  type Suggestions,
  producerListResponseSchema,
  type ProducerStanding,
  type ProducerSummary,
  type UpdateWineRequest,
  ownRatingSchema,
  type OwnRating,
  wineRatingSchema,
  type WineRating,
  type SaveTastingNoteRequest,
  type SaveWineNoteRequest,
  type TastingNoteSummary,
  tastingNoteSummarySchema,
  type SaveWineRatingRequest,
  type UpdateLotRequest,
  type CorrectDepartureRequest,
  moveBottlesResponseSchema,
  shelfContentsResponseSchema,
  stocktakeResponseSchema,
  type RecordStocktakeRequest,
  type ShelfContentsResponse,
  type StocktakeResponse,
  bottleActionResponseSchema,
  type BottleActionResponse,
  type ConsumeBottleRequest,
  type DepartBottleRequest,
  type MoveBottlesRequest,
  type MoveBottlesResponse,
  type ReturnBottleRequest,
  type TakeBottleAwayRequest,
  type AcceptWindowSuggestionRequest,
  type WindowSuggestion,
  windowSuggestionSchema,
  servingMovesResponseSchema,
  type ServingMovesResponse,
  mealSuggestionResponseSchema,
  type MealSuggestionRequest,
  type MealSuggestionResponse,
  cellarGuestsResponseSchema,
  inviteGuestResponseSchema,
  type InviteGuestRequest,
  type CellarGuestsResponse,
  type SetGuestVisibilityRequest,
  type InviteGuestResponse,
  winePhotoSchema,
  type WinePhoto,
  type UploadWinePhoto,
  qualityReportListResponseSchema,
  qualityReportSchema,
  type AcceptQualityStandingsRequest,
  type QualityReport,
  type QualityReportListResponse,
} from "@corklog/shared";

/**
 * Every response is parsed with the same Zod schema the server validated
 * against, so a contract that drifts fails here — loudly, at the boundary —
 * rather than as `undefined` three components deep.
 *
 * Requests are same-origin: Vite proxies /api to the API in development and
 * Cloudflare Pages proxies it to Cloud Run in production, so the session
 * cookie needs no special handling and there is no CORS.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(`${status} ${code}`);
    this.name = "ApiError";
  }
}

async function request(path: string, init?: RequestInit): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...init,
      headers: { "Content-Type": "application/json", ...init?.headers },
    });
  } catch {
    // fetch only rejects when the request never got an answer.
    throw new ApiError(0, "unreachable");
  }

  if (response.status === 204) return null;

  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const code =
      typeof body === "object" && body !== null && "error" in body
        ? String((body as { error: unknown }).error)
        : "unknown";
    throw new ApiError(response.status, code);
  }
  return body;
}

export async function logIn(credentials: LoginRequest): Promise<SessionUser> {
  const body = await request("/auth/login", {
    method: "POST",
    body: JSON.stringify(credentials),
  });
  return sessionResponseSchema.parse(body).user;
}

export async function logOut(): Promise<void> {
  await request("/auth/logout", { method: "POST" });
}

/** The reader has read what is new: the session back, with the day moved. */
export async function markReleaseNotesSeen(): Promise<SessionUser> {
  const body = await request("/auth/release-notes-seen", { method: "PUT" });
  return sessionResponseSchema.parse(body).user;
}

export async function fetchCurrentUser(): Promise<SessionUser | null> {
  try {
    const body = await request("/auth/me");
    return sessionResponseSchema.parse(body).user;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

/**
 * The cellars this account reaches, and the ones somebody is trying to hand it.
 *
 * The whole response rather than its `cellars` array, since `9.8`: an offer of
 * a cellar is not a cellar — accepting it is what makes it one — and the front
 * page is where it has to be answered, because nothing is mailed.
 */
export async function fetchCellars(): Promise<CellarListResponse> {
  const body = await request("/cellars");
  return cellarListResponseSchema.parse(body);
}

/**
 * Task 9.8 — the offers out of one cellar, and what has become of them.
 *
 * Owner-only behind the API, like the guest list it is reached from: who owns
 * a cellar is not something a guest arranges.
 */
export async function fetchTransfers(cellarId: string): Promise<CellarTransfersResponse> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/transfer`);
  return cellarTransfersResponseSchema.parse(body);
}

/**
 * Offering the cellar to an account. 404 is *that address has no account here*
 * and is the one refusal worth its own sentence on the screen: unlike an
 * invitation, this cannot wait for one, because what it waits for is an answer.
 */
export async function offerTransfer(
  cellarId: string,
  email: string,
): Promise<CellarTransfer> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/transfer`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
  return cellarTransferSchema.parse(body);
}

/** Taking the offer back, for as long as it has not been answered. */
export async function withdrawTransfer(
  cellarId: string,
  transferId: string,
): Promise<void> {
  await request(
    `/cellars/${encodeURIComponent(cellarId)}/transfer/${encodeURIComponent(transferId)}`,
    { method: "DELETE" },
  );
}

/**
 * Saying yes, which is the moment the cellar changes hands.
 *
 * Not under the cellar, because this account has no access to it until this
 * call returns — the offer is what authorises the request. What comes back is
 * the cellar as the new owner's list would show it, so the screen can open it
 * straight away.
 */
export async function acceptTransfer(transferId: string): Promise<CellarSummary> {
  const body = await request(`/transfers/${encodeURIComponent(transferId)}/accept`, {
    method: "POST",
  });
  return cellarSummarySchema.parse(body);
}

export async function declineTransfer(transferId: string): Promise<void> {
  await request(`/transfers/${encodeURIComponent(transferId)}/decline`, {
    method: "POST",
  });
}

/**
 * One cellar. The wine page needs it for the scale its own tasting notes are
 * scored on — the number in the score box is meaningless without it.
 */
export async function fetchCellar(cellarId: string): Promise<CellarSummary> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}`);
  return cellarSummarySchema.parse(body);
}

export async function fetchStorages(cellarId: string): Promise<StorageSummary[]> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/storages`);
  return storageListResponseSchema.parse(body).storages;
}

export async function createStorage(
  cellarId: string,
  storage: CreateStorageRequest,
): Promise<StorageSummary> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/storages`, {
    method: "POST",
    body: JSON.stringify(storage),
  });
  return storageSummarySchema.parse(body);
}

export async function createShelf(
  cellarId: string,
  storageId: string,
  shelf: CreateShelfRequest,
): Promise<ShelfSummary> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/storages/${encodeURIComponent(storageId)}/shelves`,
    { method: "POST", body: JSON.stringify(shelf) },
  );
  return shelfSummarySchema.parse(body);
}

/**
 * What a cabinet, or one shelf in it, is set to.
 *
 * `null` is sent as a value and not left out: it is how a figure that turned
 * out to be wrong is taken back, leaving *not known* rather than a stale
 * number that everything downstream would keep believing.
 */
export async function setStorageTemperature(
  cellarId: string,
  storageId: string,
  temperatureC: number | null,
): Promise<StorageSummary> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/storages/${encodeURIComponent(storageId)}`,
    { method: "PATCH", body: JSON.stringify({ temperatureC }) },
  );
  return storageSummarySchema.parse(body);
}

export async function setShelfTemperature(
  cellarId: string,
  storageId: string,
  shelfId: string,
  temperatureC: number | null,
): Promise<ShelfSummary> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/storages/${encodeURIComponent(storageId)}/shelves/${encodeURIComponent(shelfId)}`,
    { method: "PATCH", body: JSON.stringify({ temperatureC }) },
  );
  return shelfSummarySchema.parse(body);
}

/**
 * The cellar's wines, and what else the front page's one request answers.
 *
 * Two functions over one endpoint rather than two requests: `fetchWines` is
 * this with everything but the rows thrown away, which is all four of the
 * screens that read the list a second way want. Only the cellar's front page
 * needs the count of unread notes, and it is the page whose request this is.
 */
export async function fetchWineList(cellarId: string): Promise<WineListResponse> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/wines`);
  return wineListResponseSchema.parse(body);
}

export async function fetchWines(cellarId: string): Promise<WineListRow[]> {
  return (await fetchWineList(cellarId)).wines;
}

/**
 * I have seen the catalogue.
 *
 * The twin of `markCellarNotesRead` below and it exists for that reason: the
 * bookmark is a write, and a GET that quietly performed one would be a write
 * behind a verb that promises not to. Sent once the list is on screen, so the
 * marks stay for the visit that earned them rather than clearing as they are
 * drawn.
 *
 * **Failure is swallowed by the caller and nothing is said.** The worst a
 * lost bookmark can do is show the same news twice, which is the harmless
 * direction — and a cellar's front page is the last screen that should carry
 * an error about a timestamp nobody asked to move.
 */
export async function markWinesSeen(cellarId: string): Promise<void> {
  await request(`/cellars/${encodeURIComponent(cellarId)}/wines/read`, {
    method: "POST",
  });
}

/**
 * Every tasting note in the cellar, and how many of them are new to whoever is
 * asking.
 *
 * The one view that answers *has anybody written anything* — a note has only
 * ever been legible on the page of the wine it was written on, which cannot
 * answer that at all for a cellar of a hundred and twenty-three wines.
 */
export async function fetchRecentlyDrunk(
  cellarId: string,
): Promise<RecentlyDrunkResponse> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/drunk`);
  return recentlyDrunkResponseSchema.parse(body);
}

export async function fetchCellarNotes(cellarId: string): Promise<CellarNotesResponse> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/notes`);
  return cellarNotesResponseSchema.parse(body);
}

/**
 * I have looked.
 *
 * Its own call rather than a side effect of reading the list, because moving
 * the bookmark is a write and a GET that quietly performed one would be a
 * write behind a verb that promises not to. Sent once the list is on screen,
 * so the marks stay for the visit that earned them.
 */
export async function markCellarNotesRead(cellarId: string): Promise<void> {
  await request(`/cellars/${encodeURIComponent(cellarId)}/notes/read`, {
    method: "POST",
  });
}

export async function createWine(
  cellarId: string,
  wine: CreateWineRequest,
): Promise<WineSummary> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/wines`, {
    method: "POST",
    body: JSON.stringify(wine),
  });
  return wineSummarySchema.parse(body);
}

/**
 * The caller's own wishes for wines not yet owned — task 10.64. Private to the
 * account that made them, so there is no cellar-wide list to ask for.
 */
const wishesPath = (cellarId: string) =>
  `/cellars/${encodeURIComponent(cellarId)}/wishes`;

export async function fetchWishes(cellarId: string): Promise<Wish[]> {
  const body = await request(wishesPath(cellarId));
  return wishListResponseSchema.parse(body).wishes;
}

export async function createWish(cellarId: string, wish: WishRequest): Promise<Wish> {
  const body = await request(wishesPath(cellarId), {
    method: "POST",
    body: JSON.stringify(wish),
  });
  return wishSchema.parse(body);
}

export async function updateWish(
  cellarId: string,
  wishId: string,
  wish: WishRequest,
): Promise<Wish> {
  const body = await request(`${wishesPath(cellarId)}/${encodeURIComponent(wishId)}`, {
    method: "PUT",
    body: JSON.stringify(wish),
  });
  return wishSchema.parse(body);
}

/** The wish was bought, and this is the wine the registration made. */
export async function meetWish(
  cellarId: string,
  wishId: string,
  wineId: string,
): Promise<Wish> {
  const body = await request(
    `${wishesPath(cellarId)}/${encodeURIComponent(wishId)}/met`,
    {
      method: "POST",
      body: JSON.stringify({ wineId }),
    },
  );
  return wishSchema.parse(body);
}

export async function deleteWish(cellarId: string, wishId: string): Promise<void> {
  await request(`${wishesPath(cellarId)}/${encodeURIComponent(wishId)}`, {
    method: "DELETE",
  });
}

export async function fetchWine(cellarId: string, wineId: string): Promise<WineDetail> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/wines/${encodeURIComponent(wineId)}`,
  );
  return wineDetailSchema.parse(body);
}

export async function createLot(
  cellarId: string,
  wineId: string,
  lot: CreateLotRequest,
): Promise<LotSummary> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/wines/${encodeURIComponent(wineId)}/lots`,
    { method: "POST", body: JSON.stringify(lot) },
  );
  return lotSummarySchema.parse(body);
}

/**
 * Delete a lot registered by mistake, while it is still one.
 *
 * No response body and nothing to parse: the lot is gone, and the page it was
 * on refetches to find out what is left. The refusal is what carries meaning
 * here — 409 means something has happened to one of its bottles and the door
 * has closed.
 */
export async function deleteLot(
  cellarId: string,
  wineId: string,
  lotId: string,
): Promise<void> {
  await request(
    `/cellars/${encodeURIComponent(cellarId)}/wines/${encodeURIComponent(wineId)}/lots/${encodeURIComponent(lotId)}`,
    { method: "DELETE" },
  );
}

export async function fetchSuggestions(cellarId: string): Promise<Suggestions> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/suggestions`);
  return suggestionsSchema.parse(body);
}

export async function fetchStatistics(cellarId: string): Promise<CellarStatistics> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/statistics`);
  return cellarStatisticsSchema.parse(body);
}

export async function fetchProducers(cellarId: string): Promise<ProducerSummary[]> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/producers`);
  return producerListResponseSchema.parse(body).producers;
}

export async function updateProducerStanding(
  cellarId: string,
  producerId: string,
  standing: ProducerStanding,
): Promise<void> {
  await request(
    `/cellars/${encodeURIComponent(cellarId)}/producers/${encodeURIComponent(producerId)}`,
    { method: "PATCH", body: JSON.stringify({ standing }) },
  );
}

/**
 * Who reaches this cellar, and inviting somebody who does not yet.
 *
 * Both are the owner's, so both answer 403 to a guest — which the screen has
 * to say in a sentence rather than as a failed load, because a guest following
 * the link in the row of sections has done nothing wrong.
 */
export async function fetchGuests(cellarId: string): Promise<CellarGuestsResponse> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/guests`);
  return cellarGuestsResponseSchema.parse(body);
}

/**
 * Invite an address, and say on what footing.
 *
 * The role is sent rather than defaulted here, so the screen is the one place
 * that decides — a default in this function would be a second answer to a
 * question the form has already asked.
 */
export async function inviteGuest(
  cellarId: string,
  email: string,
  role: InviteGuestRequest["role"],
): Promise<InviteGuestResponse> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/guests`, {
    method: "POST",
    body: JSON.stringify({ email, role }),
  });
  return inviteGuestResponseSchema.parse(body);
}

/**
 * Your own note on a bottle, written or corrected.
 *
 * Task `9.3` — the one write a guest may make, and the author is the session
 * rather than anything in the body, so there is no request that files a note
 * in somebody else's name. Emptying all three fields removes it, which is why
 * there is no separate delete here: the form that clears it is the form that
 * wrote it.
 */
export async function saveMyTastingNote(
  cellarId: string,
  bottleId: string,
  note: SaveTastingNoteRequest,
): Promise<void> {
  await request(
    `/cellars/${encodeURIComponent(cellarId)}/bottles/${encodeURIComponent(bottleId)}/note`,
    { method: "PUT", body: JSON.stringify(note) },
  );
}

/**
 * `10.25` — notes about a wine drunk somewhere else: written, corrected and
 * removed one at a time, because a wine carries one per evening. Each answers
 * with the note as stored, except removal, which answers nothing.
 */
function wineNotesPath(cellarId: string, wineId: string): string {
  return `/cellars/${encodeURIComponent(cellarId)}/wines/${encodeURIComponent(wineId)}/notes`;
}

export async function createWineNote(
  cellarId: string,
  wineId: string,
  note: SaveWineNoteRequest,
): Promise<TastingNoteSummary> {
  const body = await request(wineNotesPath(cellarId, wineId), {
    method: "POST",
    body: JSON.stringify(note),
  });
  return tastingNoteSummarySchema.parse(body);
}

export async function updateWineNote(
  cellarId: string,
  wineId: string,
  noteId: string,
  note: SaveWineNoteRequest,
): Promise<TastingNoteSummary> {
  const body = await request(
    `${wineNotesPath(cellarId, wineId)}/${encodeURIComponent(noteId)}`,
    { method: "PUT", body: JSON.stringify(note) },
  );
  return tastingNoteSummarySchema.parse(body);
}

export async function deleteWineNote(
  cellarId: string,
  wineId: string,
  noteId: string,
): Promise<void> {
  await request(`${wineNotesPath(cellarId, wineId)}/${encodeURIComponent(noteId)}`, {
    method: "DELETE",
  });
}

/**
 * The six lists of words this cellar has decided, and correcting one of them.
 *
 * `renameVocabulary` is one call for two outcomes, because the database offers
 * one: a name no other row holds renames, and a name another row holds merges
 * into it. The answer says which happened and what moved, so the screen
 * reports what was done rather than what was asked for.
 */
export async function fetchVocabulary(cellarId: string): Promise<VocabularyResponse> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/vocabulary`);
  return vocabularyResponseSchema.parse(body);
}

export async function renameVocabulary(
  cellarId: string,
  kind: VocabularyKind,
  id: string,
  name: string,
): Promise<RenameVocabularyResponse> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/vocabulary/${kind}/${encodeURIComponent(id)}`,
    { method: "PUT", body: JSON.stringify({ name }) },
  );
  return renameVocabularyResponseSchema.parse(body);
}

/**
 * Taking access back, and withdrawing one that was never taken up.
 *
 * Two calls because they are two facts. Revoking writes a timestamp on an
 * access row that has been used, so the notes a guest wrote keep an explicable
 * origin; withdrawing deletes an invitation nobody ever claimed, which granted
 * nothing and so has nothing to preserve.
 *
 * Neither asks first. Re-inviting is the undo and the screen offers it in the
 * answer, which is the pattern the bottle actions set.
 */
export async function revokeGuest(cellarId: string, userId: string): Promise<void> {
  await request(
    `/cellars/${encodeURIComponent(cellarId)}/guests/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
}

export async function withdrawInvitation(
  cellarId: string,
  invitationId: string,
): Promise<void> {
  await request(
    `/cellars/${encodeURIComponent(cellarId)}/guests/invitations/${encodeURIComponent(invitationId)}`,
    { method: "DELETE" },
  );
}

/**
 * What one person on that page is shown of the cellar.
 *
 * Both switches, sent whole, because what this addresses is *what Erik sees*
 * and that is two booleans — the same reasoning the cellar-wide setting used
 * when it was one, and it is in `setGuestVisibilityRequestSchema`.
 *
 * The answer is not parsed back into a screen state: this decides what
 * somebody else is sent, so the page reloads itself from the server rather
 * than assuming it now knows what another reader will see.
 */
export async function setGuestVisibility(
  cellarId: string,
  userId: string,
  visibility: SetGuestVisibilityRequest,
): Promise<void> {
  await request(
    `/cellars/${encodeURIComponent(cellarId)}/guests/${encodeURIComponent(userId)}/visibility`,
    { method: "PUT", body: JSON.stringify(visibility) },
  );
}

/**
 * Bottles are moved through the cellar rather than through a wine: where a
 * bottle stands is a fact about the bottle, and the same call will empty a
 * shelf across several wines when there is a screen that asks for it.
 */
export async function moveBottles(
  cellarId: string,
  move: MoveBottlesRequest,
): Promise<MoveBottlesResponse> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/bottles/move`, {
    method: "POST",
    body: JSON.stringify(move),
  });
  return moveBottlesResponseSchema.parse(body);
}

/**
 * Drinking a bottle, and taking that back. The response of the first is what
 * the second is given: undo names the act, because a bottle can be wrong in
 * more than one way and only the event knows which one just happened.
 */
export async function consumeBottle(
  cellarId: string,
  bottleId: string,
  consumption: ConsumeBottleRequest,
): Promise<BottleActionResponse> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/bottles/${encodeURIComponent(bottleId)}/consume`,
    { method: "POST", body: JSON.stringify(consumption) },
  );
  return bottleActionResponseSchema.parse(body);
}

/**
 * The three other ways a bottle leaves its shelf: for good, for the evening,
 * and back again.
 *
 * Each returns the same shape a consumption does, so the page that offers
 * *ångra* offers it after any of them without knowing which one happened.
 */
export async function departBottle(
  cellarId: string,
  bottleId: string,
  departure: DepartBottleRequest,
): Promise<BottleActionResponse> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/bottles/${encodeURIComponent(bottleId)}/depart`,
    { method: "POST", body: JSON.stringify(departure) },
  );
  return bottleActionResponseSchema.parse(body);
}

export async function takeBottleAway(
  cellarId: string,
  bottleId: string,
  trip: TakeBottleAwayRequest,
): Promise<BottleActionResponse> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/bottles/${encodeURIComponent(bottleId)}/away`,
    { method: "POST", body: JSON.stringify(trip) },
  );
  return bottleActionResponseSchema.parse(body);
}

export async function returnBottle(
  cellarId: string,
  bottleId: string,
  homecoming: ReturnBottleRequest,
): Promise<BottleActionResponse> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/bottles/${encodeURIComponent(bottleId)}/return`,
    { method: "POST", body: JSON.stringify(homecoming) },
  );
  return bottleActionResponseSchema.parse(body);
}

export async function revertBottleEvent(
  cellarId: string,
  eventId: string,
): Promise<BottleActionResponse> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/bottle-events/${encodeURIComponent(eventId)}/revert`,
    { method: "POST" },
  );
  return bottleActionResponseSchema.parse(body);
}

/**
 * What you think of this wine, on the cellar's scale. Null clears today's
 * reading rather than recording a nought; earlier readings stand, because they
 * were true on the day they were taken.
 */
export async function setOwnRating(
  cellarId: string,
  wineId: string,
  score: number | null,
): Promise<OwnRating | null> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/wines/${encodeURIComponent(wineId)}/own-rating`,
    { method: "PUT", body: JSON.stringify({ score }) },
  );
  const parsed = z_ownRating(body);
  return parsed;
}

function z_ownRating(body: unknown): OwnRating | null {
  const wrapped = body as { ownRating?: unknown } | null;
  if (wrapped == null || wrapped.ownRating == null) return null;
  return ownRatingSchema.parse(wrapped.ownRating);
}

export async function updateWine(
  cellarId: string,
  wineId: string,
  wine: UpdateWineRequest,
): Promise<WineSummary> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/wines/${encodeURIComponent(wineId)}`,
    { method: "PATCH", body: JSON.stringify(wine) },
  );
  return wineSummarySchema.parse(body);
}

/**
 * `10.24` — say which facts this wine's label was read for and does not
 * print, as the whole set. Answers with the set as stored.
 */
export async function setLabelSilence(
  cellarId: string,
  wineId: string,
  facts: readonly LabelFact[],
): Promise<LabelFact[]> {
  const payload: SetLabelSilenceRequest = { facts: [...facts] };
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/wines/${encodeURIComponent(wineId)}/label-silence`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
  return labelSilenceResponseSchema.parse(body).labelSilentOn;
}

/**
 * Correct a lot's own facts.
 *
 * Separate from `createLot` rather than one call with an optional id, because
 * they are different acts with different rules: creating a lot creates bottles,
 * and correcting one may not. The request carries no quantity for exactly that
 * reason — see `updateLotRequestSchema`.
 */
export async function updateLot(
  cellarId: string,
  wineId: string,
  lotId: string,
  lot: UpdateLotRequest,
): Promise<LotSummary> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/wines/${encodeURIComponent(wineId)}/lots/${encodeURIComponent(lotId)}`,
    { method: "PATCH", body: JSON.stringify(lot) },
  );
  return lotSummarySchema.parse(body);
}

/** Record what a source says about this wine, or correct today's reading. */
export async function saveWineRating(
  cellarId: string,
  wineId: string,
  rating: SaveWineRatingRequest,
): Promise<WineRating> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/wines/${encodeURIComponent(wineId)}/ratings`,
    { method: "PUT", body: JSON.stringify(rating) },
  );
  return wineRatingSchema.parse(body);
}

export async function deleteWineRating(
  cellarId: string,
  wineId: string,
  ratingId: string,
): Promise<void> {
  await request(
    `/cellars/${encodeURIComponent(cellarId)}/wines/${encodeURIComponent(wineId)}/ratings/${encodeURIComponent(ratingId)}`,
    { method: "DELETE" },
  );
}

/**
 * Correct the date and the words on a departure.
 *
 * Not an undo: that a bottle left stands, and only what was written about it
 * changes. Nothing comes back — the wine's page refetches, because a corrected
 * date can reorder the notes it is shown among.
 */
export async function correctDeparture(
  cellarId: string,
  bottleId: string,
  correction: CorrectDepartureRequest,
): Promise<void> {
  await request(
    `/cellars/${encodeURIComponent(cellarId)}/bottles/${encodeURIComponent(bottleId)}/departure`,
    { method: "PATCH", body: JSON.stringify(correction) },
  );
}

/**
 * Ask for a drinking window. A write, because asking is recorded whether or
 * not any of it is taken — see the route.
 */
export async function askForWindow(
  cellarId: string,
  wineId: string,
): Promise<WindowSuggestion> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/wines/${encodeURIComponent(wineId)}/window-suggestions`,
    { method: "POST", body: JSON.stringify({}) },
  );
  return windowSuggestionSchema.parse(body);
}

/**
 * Take some of it. A field left out is left alone; a field present carries the
 * year that is to stand on the wine, whether that is the suggested one or an
 * edit of it. Which of the two it was is decided by the server, by comparing —
 * this request cannot claim a provenance.
 */
export async function acceptWindowSuggestion(
  cellarId: string,
  wineId: string,
  suggestionId: string,
  years: AcceptWindowSuggestionRequest,
): Promise<WineSummary> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/wines/${encodeURIComponent(wineId)}/window-suggestions/${encodeURIComponent(suggestionId)}/accept`,
    { method: "POST", body: JSON.stringify(years) },
  );
  return wineSummarySchema.parse(body);
}

/**
 * Walking a shelf: what the record says is on it, and what was actually found.
 *
 * The contents are read fresh at the start of a walk rather than taken from
 * the wine list already in hand. The list says where a wine's bottles stand;
 * it does not say which bottles those are, and a walk writes events against
 * objects.
 */
export async function fetchShelfContents(
  cellarId: string,
  shelfId: string,
): Promise<ShelfContentsResponse> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/shelves/${encodeURIComponent(shelfId)}/bottles`,
  );
  return shelfContentsResponseSchema.parse(body);
}

/**
 * What should be carried to the serving cabinet, and where there is room.
 *
 * Its own request rather than a reading of the wine list, because neither
 * thing it turns on is in that list: which cabinet is the fridge, and which
 * actual bottles are standing in the other one. The move that follows is the
 * ordinary `moveBottles` above — this route proposes and never writes.
 */
export async function fetchServingMoves(cellarId: string): Promise<ServingMovesResponse> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/serving-moves`);
  return servingMovesResponseSchema.parse(body);
}

/**
 * Which bottle suits tonight's meal — task `10.1`.
 *
 * A POST that stores nothing, which the route explains: a pairing is advice for
 * one evening rather than a claim about a bottle. The answer names wines by id,
 * so the page draws each row from the list it already has and a wine the model
 * invented cannot reach the screen.
 */
export async function suggestForMeal(
  cellarId: string,
  dish: string,
): Promise<MealSuggestionResponse> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/meal-suggestions`,
    {
      method: "POST",
      body: JSON.stringify({ dish } satisfies MealSuggestionRequest),
    },
  );
  return mealSuggestionResponseSchema.parse(body);
}

/**
 * The quality report: its history, one report, asking for a new one, and
 * accepting the producer standings it proposes. `10.26`.
 *
 * Asking answers `429 too_soon` inside the hour and `503 not_configured` with
 * no key; both arrive as an `ApiError` whose code the page turns into a
 * sentence. When it may be asked again is on the history, not on the refusal,
 * so the page reloads that rather than reading the error body.
 */
export async function fetchQualityReports(
  cellarId: string,
): Promise<QualityReportListResponse> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/quality-reports`);
  return qualityReportListResponseSchema.parse(body);
}

export async function fetchQualityReport(
  cellarId: string,
  reportId: string,
): Promise<QualityReport> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/quality-reports/${encodeURIComponent(reportId)}`,
  );
  return qualityReportSchema.parse(body);
}

export async function askForQualityReport(cellarId: string): Promise<QualityReport> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/quality-reports`, {
    method: "POST",
  });
  return qualityReportSchema.parse(body);
}

export async function acceptQualityStandings(
  cellarId: string,
  reportId: string,
  producerRowIds: string[],
): Promise<QualityReport> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/quality-reports/${encodeURIComponent(reportId)}/accept`,
    {
      method: "POST",
      body: JSON.stringify({ producerRowIds } satisfies AcceptQualityStandingsRequest),
    },
  );
  return qualityReportSchema.parse(body);
}

/**
 * The administrator's six switches for the three questions to the model.
 * Anybody else is answered 404, which the page never reaches: its link is
 * drawn only for the administrator. See `routes/system.ts`.
 */
export async function fetchSystemSettings(): Promise<SystemSettings> {
  const body = await request("/system/settings");
  return systemSettingsSchema.parse(body);
}

/** Every account and when it was last in — the same page, the administrator's. */
export async function fetchSystemAccounts(): Promise<SystemAccountListResponse> {
  const body = await request("/system/accounts");
  return systemAccountListResponseSchema.parse(body);
}

/** Every cellar, and how each answers the three questions — the same page. */
export async function fetchSystemCellars(): Promise<SystemCellarListResponse> {
  const body = await request("/system/cellars");
  return systemCellarListResponseSchema.parse(body);
}

export async function saveCellarOverrides(
  cellarId: string,
  overrides: CellarOverrides,
): Promise<CellarOverrides> {
  const body = await request(
    `/system/cellars/${encodeURIComponent(cellarId)}/overrides`,
    {
      method: "PUT",
      body: JSON.stringify(overrides satisfies CellarOverrides),
    },
  );
  return cellarOverridesSchema.parse(body);
}

/** The caller's own switch for the weekly wish search. 403 when it cannot be
 *  turned on: the administrator has not allowed it, or the account owns no
 *  cellar. */
export async function saveWishSearch(enabled: boolean): Promise<WishSearchSetting> {
  const body = await request("/wish-search", {
    method: "PUT",
    body: JSON.stringify({ enabled } satisfies WishSearchRequest),
  });
  return wishSearchSettingSchema.parse(body);
}

/** Search the caller's open wishes now, four at a time. Takes most of a minute. */
export async function searchWishesNow(): Promise<WishSearchNowResponse> {
  const body = await request("/wish-search/now", { method: "POST" });
  return wishSearchNowResponseSchema.parse(body);
}

/** The administrator runs one tick of the weekly search now. 409 when the
 *  allowance is off. Takes most of a minute. */
export async function runWishSearchTickNow(): Promise<WishSearchNowResponse> {
  const body = await request("/system/wish-search/run", { method: "POST" });
  return wishSearchNowResponseSchema.parse(body);
}

export async function saveSystemSettings(
  settings: SystemSettings,
): Promise<SystemSettings> {
  const body = await request("/system/settings", {
    method: "PUT",
    body: JSON.stringify(settings satisfies SystemSettings),
  });
  return systemSettingsSchema.parse(body);
}

/**
 * Descriptions of wine areas — the fourth question, from 2026-09-28. Reading
 * what is published is anybody's; the rest is the administrator's and answers
 * anybody else 404. See `routes/area-reports.ts`.
 */
export async function fetchPublishedAreaReports(): Promise<PublishedAreaReportListResponse> {
  const body = await request("/area-reports");
  return publishedAreaReportListResponseSchema.parse(body);
}

/** The names in every published report, for linking to them. */
export async function fetchAreaReportIndex(): Promise<AreaReportIndexResponse> {
  const body = await request("/area-reports/index");
  return areaReportIndexResponseSchema.parse(body);
}

export async function fetchAllAreaReports(): Promise<AreaReportAdminListResponse> {
  const body = await request("/area-reports/all");
  return areaReportAdminListResponseSchema.parse(body);
}

export async function fetchAreaReport(reportId: string): Promise<AreaReport> {
  const body = await request(`/area-reports/${encodeURIComponent(reportId)}`);
  return areaReportSchema.parse(body);
}

/** Run one. Takes as long as the model does — half a minute or so. */
export async function runAreaReport(run: RunAreaReportRequest): Promise<AreaReport> {
  const body = await request("/area-reports", {
    method: "POST",
    body: JSON.stringify(run satisfies RunAreaReportRequest),
  });
  return areaReportSchema.parse(body);
}

export async function publishAreaReport(reportId: string): Promise<AreaReportSummary> {
  const body = await request(`/area-reports/${encodeURIComponent(reportId)}/publish`, {
    method: "POST",
  });
  return areaReportSummarySchema.parse(body);
}

/** Take a published report back to a draft; the area leaves the tree. */
export async function unpublishAreaReport(reportId: string): Promise<AreaReportSummary> {
  const body = await request(`/area-reports/${encodeURIComponent(reportId)}/unpublish`, {
    method: "POST",
  });
  return areaReportSummarySchema.parse(body);
}

/** Fact-check a report against the web — about a minute. The administrator's. */
export async function reviewAreaReport(reportId: string): Promise<AreaReport> {
  const body = await request(`/area-reports/${encodeURIComponent(reportId)}/review`, {
    method: "POST",
  });
  return areaReportSchema.parse(body);
}

/** Apply the fact check's fixes the administrator accepted. */
export async function applyAreaFindings(
  reportId: string,
  apply: ApplyAreaFindingsRequest,
): Promise<AreaReport> {
  const body = await request(
    `/area-reports/${encodeURIComponent(reportId)}/review/apply`,
    { method: "POST", body: JSON.stringify(apply satisfies ApplyAreaFindingsRequest) },
  );
  return areaReportSchema.parse(body);
}

export async function deleteAreaReport(reportId: string): Promise<void> {
  await request(`/area-reports/${encodeURIComponent(reportId)}`, { method: "DELETE" });
}

/** The reader's own wishes for area reports, and what became of them. */
export async function fetchMyAreaWishes(): Promise<MyAreaWishListResponse> {
  const body = await request("/area-wishes/mine");
  return myAreaWishListResponseSchema.parse(body);
}

/**
 * Wish for a report. A report already published answers 409
 * `already_published`, which the page turns into a link to it.
 */
export async function wishAreaReport(wish: WishAreaReportRequest): Promise<void> {
  await request("/area-wishes", {
    method: "POST",
    body: JSON.stringify(wish satisfies WishAreaReportRequest),
  });
}

/** Every open wish, gathered by area — the administrator's. */
export async function fetchOpenAreaWishes(): Promise<OpenAreaWishListResponse> {
  const body = await request("/area-wishes/open");
  return openAreaWishListResponseSchema.parse(body);
}

export async function declineAreaWishes(
  decline: DeclineAreaWishesRequest,
): Promise<void> {
  await request("/area-wishes/decline", {
    method: "POST",
    body: JSON.stringify(decline satisfies DeclineAreaWishesRequest),
  });
}

export async function recordStocktake(
  cellarId: string,
  walk: RecordStocktakeRequest,
): Promise<StocktakeResponse> {
  const body = await request(`/cellars/${encodeURIComponent(cellarId)}/stocktakes`, {
    method: "POST",
    body: JSON.stringify(walk),
  });
  return stocktakeResponseSchema.parse(body);
}

/**
 * Photographs of the bottle, held against the wine.
 *
 * Both sizes travel in one request. A wine with a full-size photograph and no
 * thumbnail is a state nothing wants to handle, and a second request is a
 * second chance to fail halfway — so `resizeForUpload` makes both and this
 * sends both.
 */
export async function uploadWinePhoto(
  cellarId: string,
  wineId: string,
  photo: UploadWinePhoto,
): Promise<WinePhoto> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/wines/${encodeURIComponent(wineId)}/photos`,
    { method: "POST", body: JSON.stringify(photo) },
  );
  return winePhotoSchema.parse(body);
}

/** Make this one the wine's face: shown first on its page. */
export async function designateWinePhoto(
  cellarId: string,
  wineId: string,
  photoId: string,
): Promise<WinePhoto> {
  const body = await request(
    `/cellars/${encodeURIComponent(cellarId)}/wines/${encodeURIComponent(wineId)}/photos/${encodeURIComponent(photoId)}`,
    { method: "PATCH" },
  );
  return winePhotoSchema.parse(body);
}

/** Remove one, bytes and all. The server is what makes that true of the
 *  bucket as well as the row. */
export async function deleteWinePhoto(
  cellarId: string,
  wineId: string,
  photoId: string,
): Promise<void> {
  await request(
    `/cellars/${encodeURIComponent(cellarId)}/wines/${encodeURIComponent(wineId)}/photos/${encodeURIComponent(photoId)}`,
    { method: "DELETE" },
  );
}
